<template>
    <div class="ViewsGrid">
        <Views
            v-if="active"
            v-show="visible"
            :value="active"
            :views="views"
            :options="options"
            :dropdownDirection="dropdownDirection"
            @onViewChanged="handleViewChanged"
            @onAddView="handleAddView"
            @onOptionClicked="handleViewOptionClicked"
        />

        <Modal
            :opened="modal === modals.ADD_VIEW"
            :title="$t(`entities.actions.addView`)"
            #body
            @close="handleCleanModal"
        >
            <ViewForm
                :loading="isLoading"
                @onCancel="handleCleanModal"
                @onSubmit="addView"
            />
        </Modal>

        <Modal
            :opened="modal === modals.UPDATE_VIEW"
            :title="$t(`entities.actions.updateView`)"
            #body
            @close="handleCleanModal"
        >
            <ViewForm
                :value="((selected || {}).data || {}).label"
                :loading="isLoading"
                @onCancel="handleCleanModal"
                @onSubmit="editNameView"
            />
        </Modal>

        <ModalRequest
            :opened="modal === modals.DELETE_VIEW"
            :title="$t('entities.modals.removeView.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="removeView"
        />
    </div>
</template>

<script>
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import VueTypes from 'vue-types'
import {
    VIEWS_DEFAULT_ID, VIEWS_DEFAULT, VIEWS_ACTIONS, VIEWS_OPTIONS,
} from '@/entities/views'
import { Modal, ModalRequest } from '@/components'
import Views from '../Views/Views.vue'
import ViewForm from '../ViewForm/ViewForm.vue'

export const MODAL_TYPES = {
    ADD_VIEW: 'ADD_VIEW',
    DELETE_VIEW: 'DELETE_VIEW',
    UPDATE_VIEW: 'UPDATE_VIEW',
}

export default {
    name: 'ViewsGrid',
    components: {
        Modal,
        ModalRequest,
        Views,
        ViewForm,
    },
    props: {
        value: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        grid: VueTypes.any.isRequired,
        entityKey: VueTypes.string.isRequired,
        type: VueTypes.string,
        visible: VueTypes.bool.def(true),
        dropdownDirection: VueTypes.string.def('topRight'),
    },
    data () {
        return {
            // Views
            views: VIEWS_DEFAULT,
            active: VIEWS_DEFAULT_ID,
            selected: undefined,
            isLoading: false,

            // Modals
            modal: undefined,
            modals: MODAL_TYPES,
        }
    },
    watch: {
        value: {
            handler (active) {
                this.handleViewChanged(active)
            },
        },
    },
    beforeCreate () {
        this.options = VIEWS_OPTIONS
    },
    mounted () {
        this.getViews()
    },
    methods: {
        findView (viewId) {
            return (this.views || []).find(({ id }) => id === viewId)
        },
        getViewConfig () {
            const columns = this.grid.columnController.getColumnState()
            return { columns }
        },
        setViewConfig ({ data }) {
            const { columnController } = this.grid
            const { columns } = data || {}
            // Manage columns
            if ((columns || []).length) {
                columns.forEach(({
                    colId, hide, pinned, width,
                }) => {
                    columnController.setColumnVisible(colId, !hide)
                    columnController.setColumnPinned(colId, pinned)
                    if (width) columnController.setColumnWidth(colId, width)
                })
            } else {
                columnController.autoSizeColumns()
                columnController.getAllGridColumns().forEach(({ colId }) => {
                    columnController.setColumnVisible(colId, true)
                    columnController.setColumnPinned(colId, null)
                })
            }
        },
        handleCleanModal () {
            this.modal = undefined
        },
        handleAddView () {
            this.modal = this.modals.ADD_VIEW
        },
        handleViewOptionClicked ({ action, id }) {
            this.selected = this.findView(id)
            const actions = {
                [VIEWS_ACTIONS.SAVE]: this.saveView,
                [VIEWS_ACTIONS.REMOVE]: () => (this.modal = this.modals.DELETE_VIEW),
                [VIEWS_ACTIONS.DUPLICATE]: this.duplicateView,
                [VIEWS_ACTIONS.EDIT]: () => (this.modal = this.modals.UPDATE_VIEW),
                [VIEWS_ACTIONS.DEFAULT]: this.setDefault,
            }

            if (actions[action]) actions[action]()
        },
        handleViewChanged (viewId, force) {
            if (viewId !== this.active || force) {
                const active = this.findView(viewId)
                this.active = viewId
                if (active) this.setViewConfig(active)
                this.$emit('onChange', viewId !== VIEWS_DEFAULT_ID ? viewId : undefined)
            }
        },
        /**
         * Add View
        */
        async addView (label) {
            await this.createView({ label, ...this.getViewConfig() })
        },
        /*
        * Save View
        */
        async saveView () {
            const { id, isDefault, data } = this.selected
            await this.updateView({ id, isDefault, data: { ...data, ...this.getViewConfig() } })
            this.selected = undefined
        },
        /*
        * Set Default View
        */
        async setDefault () {
            const { id, isDefault, data } = this.selected
            await this.updateView({ id, isDefault: !isDefault, data: { ...data, ...this.getViewConfig() } })
            this.selected = undefined
        },
        /*
        * Edit name View
        */
        async editNameView (label) {
            const { id, isDefault, data } = this.selected
            await this.updateView({ id, isDefault, data: { ...data, label } })
            this.selected = undefined
        },
        /*
        * Duplicate Views
        */
        async duplicateView () {
            const { data } = this.selected
            await this.createView(data)
            this.selected = undefined
        },
        /*
        * Get Views
        */
        async getViews () {
            this.isLoading = true
            apiRequest(Api().views.list({ entity: this.entityKey, ...(this.type ? { type: this.type } : {}) }))
                .then(({ data }) => {
                    const views = [...this.views, ...data]
                    const viewDefault = views.find(item => item.isDefault)
                    this.views = views
                    if (this.value || viewDefault) this.handleViewChanged((this.value || viewDefault.id), true)
                    this.$emit('onLoad', views)
                })
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        /*
        * Create Views
        */
        async createView (view) {
            this.isLoading = true
            const params = { entity: this.entityKey, ...(this.type ? { type: this.type } : {}), data: view }
            apiRequest(Api().views.create(params))
                .then(({ data }) => {
                    this.views = [...this.views, data]
                    this.handleViewChanged(data.id)
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isLoading = false
                })
        },

        /*
        * Edit Views
        */
        async updateView ({ id: viewId, isDefault, data }) {
            this.isLoading = true
            const params = { viewId, isDefault, data }
            apiRequest(Api().views.update(params))
                .then(() => {
                    const updateView = item => (item.id === viewId
                        ? { ...item, isDefault, data: { ...item.data, ...data } }
                        : { ...item, isDefault: isDefault ? false : item.isDefault })
                    this.views = [...this.views.map(updateView)]
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isLoading = false
                })
        },

        /*
        * Delete View
        */
        async removeView () {
            this.isLoading = true
            const { id: viewId } = this.selected
            apiRequest(Api().views.remove({ viewId }))
                .then(() => {
                    this.views = [...this.views.filter(({ id }) => id !== viewId)]
                    this.handleViewChanged(VIEWS_DEFAULT_ID)
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.selected = undefined
                    this.isLoading = false
                })
        },
    },
}
</script>
<style lang="scss" scoped>
.Views {
    height: 35px;
    border-top: 1px solid $gray-05;
    border-left: 1px solid $gray-05;
    border-right: 1px solid $gray-05;
}
</style>
